import { useState, useEffect } from 'react';
import { Navigation, Image, Trash2, Menu, Layers, Megaphone, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import type { KreatorSettingsData } from '../KreatorPage';
import { WpMediaPickerModal } from '../../components/WpMediaPickerModal';
import { SettingsSaveBar } from '../../components/SettingsSaveBar';
import { SectionHeader } from '../../components/SectionHeader';
import { createTenantKreatorAdapter, type KreatorApiAdapter } from '../../lib/kreator-api-adapter';
import { normalizeHexColor, prefixHexColor, safeColorForPicker } from './builder-v2/utils/color-utils';

interface Props {
  integrationId: string;
  settings: KreatorSettingsData | null;
  onSaved: () => void;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
  apiAdapter?: KreatorApiAdapter;
}

export function KreatorNavigationSection({ integrationId, settings, onSaved, showError, apiAdapter }: Props) {
  const adapter = apiAdapter ?? createTenantKreatorAdapter(integrationId);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [mediaPickerFor, setMediaPickerFor] = useState<'light' | 'dark' | null>(null);

  const [form, setForm] = useState({
    logoLightUrl: settings?.navigation.logoLightUrl || null,
    logoDarkUrl: settings?.navigation.logoDarkUrl || null,
    logoMaxHeightDesktop: settings?.navigation.logoMaxHeightDesktop || 60,
    logoMaxHeightMobile: settings?.navigation.logoMaxHeightMobile || 40,
    paddingTop: settings?.navigation.paddingTop ?? 15,
    paddingTopTablet: settings?.navigation.paddingTopTablet ?? settings?.navigation.paddingTop ?? 15,
    paddingTopMobile: settings?.navigation.paddingTopMobile ?? settings?.navigation.paddingTopTablet ?? settings?.navigation.paddingTop ?? 15,
    paddingBottom: settings?.navigation.paddingBottom ?? 15,
    paddingBottomTablet: settings?.navigation.paddingBottomTablet ?? settings?.navigation.paddingBottom ?? 15,
    paddingBottomMobile: settings?.navigation.paddingBottomMobile ?? settings?.navigation.paddingBottomTablet ?? settings?.navigation.paddingBottom ?? 15,
    menuBarColor: settings?.navigation.menuBarColor || '#ffffff',
    transparency: settings?.navigation.transparency || 100,
    blurEffect: settings?.navigation.blurEffect || false,
    blurAmount: settings?.navigation.blurAmount ?? 20,
    hideOnScroll: settings?.navigation.hideOnScroll ?? true,
    hideMobileBurgerMenu: settings?.navigation.hideMobileBurgerMenu ?? false,
    bottomBorder: settings?.navigation.bottomBorder || 'none',
    bottomBorderColor: settings?.navigation.bottomBorderColor || '#e5e7eb',
    smartMenuEnabled: settings?.navigation.smartMenuEnabled || false,
    promoBar: {
      enabled: settings?.navigation.promoBar.enabled || false,
      backgroundColor: settings?.navigation.promoBar.backgroundColor || '#1a1a2e',
      textColor: settings?.navigation.promoBar.textColor || '#ffffff',
      messages: (settings?.navigation.promoBar.messages && settings.navigation.promoBar.messages.length > 0)
        ? settings.navigation.promoBar.messages
        : [{ text: '', linkUrl: null, linkText: null }],
      autoRotate: settings?.navigation.promoBar.autoRotate ?? true,
      rotateIntervalSec: settings?.navigation.promoBar.rotateIntervalSec ?? 5,
      showArrows: settings?.navigation.promoBar.showArrows ?? true,
      animation: (settings?.navigation.promoBar.animation ?? 'fade') as 'fade' | 'slide',
      alwaysVisible: settings?.navigation.promoBar.alwaysVisible ?? false,
    },
  });

  // Promo bar live preview — current slide index
  const [previewIdx, setPreviewIdx] = useState(0);
  const messages = form.promoBar.messages;
  const validMessages = messages.filter((m) => m.text.trim() !== '');

  useEffect(() => {
    if (previewIdx >= validMessages.length && validMessages.length > 0) {
      setPreviewIdx(0);
    }
  }, [validMessages.length, previewIdx]);

  useEffect(() => {
    if (!form.promoBar.autoRotate || validMessages.length <= 1) return;
    const id = window.setInterval(() => {
      setPreviewIdx((i) => (i + 1) % validMessages.length);
    }, Math.max(2, form.promoBar.rotateIntervalSec) * 1000);
    return () => window.clearInterval(id);
  }, [form.promoBar.autoRotate, form.promoBar.rotateIntervalSec, validMessages.length]);

  const updateMessage = (idx: number, patch: Partial<{ text: string; linkUrl: string | null; linkText: string | null }>) => {
    setForm((f) => ({
      ...f,
      promoBar: {
        ...f.promoBar,
        messages: f.promoBar.messages.map((m, i) => (i === idx ? { ...m, ...patch } : m)),
      },
    }));
  };

  const addMessage = () => {
    if (form.promoBar.messages.length >= 3) return;
    setForm((f) => ({
      ...f,
      promoBar: {
        ...f.promoBar,
        messages: [...f.promoBar.messages, { text: '', linkUrl: null, linkText: null }],
      },
    }));
  };

  const removeMessage = (idx: number) => {
    setForm((f) => ({
      ...f,
      promoBar: {
        ...f.promoBar,
        messages: f.promoBar.messages.filter((_, i) => i !== idx),
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adapter.updateNavigation({
        ...form,
        logoMaxHeightDesktop: Math.max(1, Math.min(200, Number(form.logoMaxHeightDesktop) || 60)),
        logoMaxHeightMobile: Math.max(1, Math.min(120, Number(form.logoMaxHeightMobile) || 40)),
        paddingTop: Math.max(0, Math.min(100, Number(form.paddingTop) || 0)),
        paddingTopTablet: Math.max(0, Math.min(100, Number(form.paddingTopTablet) || 0)),
        paddingTopMobile: Math.max(0, Math.min(100, Number(form.paddingTopMobile) || 0)),
        paddingBottom: Math.max(0, Math.min(100, Number(form.paddingBottom) || 0)),
        paddingBottomTablet: Math.max(0, Math.min(100, Number(form.paddingBottomTablet) || 0)),
        paddingBottomMobile: Math.max(0, Math.min(100, Number(form.paddingBottomMobile) || 0)),
        menuBarColor: normalizeHexColor(form.menuBarColor),
        bottomBorderColor: form.bottomBorderColor ? normalizeHexColor(form.bottomBorderColor) : null,
        promoBar: {
          ...form.promoBar,
          backgroundColor: normalizeHexColor(form.promoBar.backgroundColor),
          textColor: normalizeHexColor(form.promoBar.textColor),
        },
      });
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
      onSaved();
      if (adapter.isTenantScoped) {
        try {
          await adapter.purgeWpCache();
        } catch (purgeErr: unknown) {
          const msg = (purgeErr as { response?: { data?: { error?: string } } })?.response?.data?.error;
          try {
            const diag = await adapter.diagnoseWp();
            showError(diag.data.ok ? 'Zapisano, ale cache nie został wyczyszczony. Wyczyść cache ręcznie w WordPress.' : `Zapisano, ale cache WP nie działa: ${diag.data.error}`);
          } catch {
            showError(msg ? `Zapisano, ale czyszczenie cache nie powiodło się: ${msg}` : 'Zapisano ustawienia, ale nie udało się wyczyścić cache WordPress.');
          }
        }
      }
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  const Toggle = ({ checked, onChange, label, description }: {
    checked: boolean;
    onChange: (v: boolean) => void;
    label: string;
    description?: string;
  }) => (
    <div className="flex items-center justify-between p-4 rounded-lg border border-border-subtle bg-surface-raised">
      <div className="flex-1 mr-4">
        <div className="text-sm font-medium text-text-primary">{label}</div>
        {description && <div className="text-xs text-text-muted mt-0.5">{description}</div>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
          checked ? 'bg-accent-primary' : 'bg-surface-muted'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
          <Navigation className="w-6 h-6 text-accent-primary" />
          Nawigacja
        </h1>
        <p className="text-text-secondary mt-1">Wygląd i zachowanie belki menu</p>
      </div>

      {/* Logo Upload */}
      <div className="card">
        <SectionHeader icon={Image} title="Logo" description="Logotypy wyświetlane w belce nawigacyjnej sklepu" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Light background logo */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Logo do jasnego tła</label>
            {form.logoLightUrl ? (
              <div className="relative inline-block p-4 bg-white rounded-lg">
                <img src={form.logoLightUrl} alt="Logo (jasne tło)" className="max-h-16 max-w-[200px] object-contain" />
                <button
                  onClick={() => setForm({ ...form, logoLightUrl: null })}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-danger rounded-full flex items-center justify-center text-white hover:bg-danger"
                  aria-label="Usuń logo do jasnego tła"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setMediaPickerFor('light')}
                className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-border rounded-lg text-text-secondary hover:text-text-primary hover:border-accent-primary/30 transition-all"
              >
                <Image className="w-4 h-4" />
                Wybierz z biblioteki
              </button>
            )}
          </div>

          {/* Dark background logo */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Logo do ciemnego tła</label>
            {form.logoDarkUrl ? (
              <div className="relative inline-block p-4 bg-surface-base rounded-lg border border-border">
                <img src={form.logoDarkUrl} alt="Logo (ciemne tło)" className="max-h-16 max-w-[200px] object-contain" />
                <button
                  onClick={() => setForm({ ...form, logoDarkUrl: null })}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-danger rounded-full flex items-center justify-center text-white hover:bg-danger"
                  aria-label="Usuń logo do ciemnego tła"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setMediaPickerFor('dark')}
                className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-border rounded-lg text-text-secondary hover:text-text-primary hover:border-accent-primary/30 transition-all"
              >
                <Image className="w-4 h-4" />
                Wybierz z biblioteki
              </button>
            )}
          </div>
        </div>

        {/* Logo dimensions */}
        <div className="mt-4 flex gap-6">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Maks. wysokość logo (desktop)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="200"
                value={form.logoMaxHeightDesktop}
                onChange={(e) => setForm({ ...form, logoMaxHeightDesktop: parseInt(e.target.value) || 60 })}
                className="input w-24"
              />
              <span className="text-sm text-text-muted">px</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Maks. wysokość logo (mobile)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="120"
                value={form.logoMaxHeightMobile}
                onChange={(e) => setForm({ ...form, logoMaxHeightMobile: parseInt(e.target.value) || 40 })}
                className="input w-24"
              />
              <span className="text-sm text-text-muted">px</span>
            </div>
          </div>
        </div>
      </div>

      {/* Menu Bar Appearance */}
      <div className="card">
        <SectionHeader icon={Menu} title="Wygląd belki menu" description="Kolory, odstępy i efekty wizualne belki nawigacyjnej" accent="secondary" />
        <div className="space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Odstęp góra desktop</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.paddingTop}
                  onChange={(e) => setForm({ ...form, paddingTop: parseInt(e.target.value) || 0 })}
                  className="input w-20"
                />
                <span className="text-sm text-text-muted">px</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Odstęp góra tablet</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.paddingTopTablet}
                  onChange={(e) => setForm({ ...form, paddingTopTablet: parseInt(e.target.value) || 0 })}
                  className="input w-20"
                />
                <span className="text-sm text-text-muted">px</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Odstęp góra mobile</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.paddingTopMobile}
                  onChange={(e) => setForm({ ...form, paddingTopMobile: parseInt(e.target.value) || 0 })}
                  className="input w-20"
                />
                <span className="text-sm text-text-muted">px</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Odstęp dół desktop</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.paddingBottom}
                  onChange={(e) => setForm({ ...form, paddingBottom: parseInt(e.target.value) || 0 })}
                  className="input w-20"
                />
                <span className="text-sm text-text-muted">px</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Odstęp dół tablet</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.paddingBottomTablet}
                  onChange={(e) => setForm({ ...form, paddingBottomTablet: parseInt(e.target.value) || 0 })}
                  className="input w-20"
                />
                <span className="text-sm text-text-muted">px</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Odstęp dół mobile</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.paddingBottomMobile}
                  onChange={(e) => setForm({ ...form, paddingBottomMobile: parseInt(e.target.value) || 0 })}
                  className="input w-20"
                />
                <span className="text-sm text-text-muted">px</span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Kolor belki menu</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={safeColorForPicker(form.menuBarColor, '#ffffff')}
                onChange={(e) => setForm({ ...form, menuBarColor: e.target.value })}
                className="w-10 h-10 rounded border border-border bg-transparent cursor-pointer"
              />
              <input
                type="text"
                value={form.menuBarColor}
                onChange={(e) => setForm({ ...form, menuBarColor: prefixHexColor(e.target.value) })}
                onBlur={(e) => setForm({ ...form, menuBarColor: normalizeHexColor(e.target.value) })}
                className="input w-28 text-sm font-mono"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Przezroczystość belki: {form.transparency}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={form.transparency}
              onChange={(e) => setForm({ ...form, transparency: parseInt(e.target.value) })}
              className="w-full max-w-xs accent-accent-primary"
            />
          </div>

          <Toggle
            checked={form.blurEffect}
            onChange={(v) => setForm({ ...form, blurEffect: v })}
            label="Blur na belce"
            description="Efekt rozmycia tła pod przezroczystą belką menu"
          />

          {form.blurEffect && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Stopień rozmycia: <span className="text-accent-primary font-mono">{form.blurAmount}px</span>
              </label>
              <input
                type="range"
                min="0"
                max="50"
                value={form.blurAmount}
                onChange={(e) => setForm({ ...form, blurAmount: parseInt(e.target.value) })}
                className="w-full max-w-xs accent-accent-primary"
              />
              <p className="text-xs text-text-muted mt-1">Intensywność efektu rozmycia tła (0 = brak, 50 = mocny)</p>
            </div>
          )}

          <Toggle
            checked={form.hideOnScroll}
            onChange={(v) => setForm({ ...form, hideOnScroll: v })}
            label="Chowaj przy przewijaniu"
            description="Nawigacja chowa się przy scrollowaniu w dół i pojawia przy scrollowaniu do góry"
          />

          <Toggle
            checked={form.hideMobileBurgerMenu}
            onChange={(v) => setForm({ ...form, hideMobileBurgerMenu: v })}
            label="Ukryj burger menu na mobile"
            description="Na urządzeniach mobilnych nie pokazuj przycisku otwierającego menu boczne"
          />

          <div className="p-4 rounded-lg border border-border-subtle bg-surface-raised">
            <label className="block text-sm font-medium text-text-primary mb-3">Border na dole belki</label>
            <div className="flex items-center gap-4">
              <select
                value={form.bottomBorder}
                onChange={(e) => setForm({ ...form, bottomBorder: e.target.value })}
                className="input w-32"
              >
                <option value="none">Brak</option>
                <option value="solid">Tak</option>
              </select>
              {form.bottomBorder === 'solid' && (
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={safeColorForPicker(form.bottomBorderColor, '#e5e7eb')}
                    onChange={(e) => setForm({ ...form, bottomBorderColor: e.target.value })}
                    className="w-8 h-8 rounded border border-border bg-transparent cursor-pointer"
                  />
                  <input
                    type="text"
                    value={form.bottomBorderColor || '#e5e7eb'}
                    onChange={(e) => setForm({ ...form, bottomBorderColor: prefixHexColor(e.target.value) })}
                    onBlur={(e) => setForm({ ...form, bottomBorderColor: normalizeHexColor(e.target.value) })}
                    className="input w-24 text-xs font-mono"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Smart Menu */}
      <div className="card">
        <SectionHeader icon={Layers} title="Smart Menu" description="Inteligentne menu kontekstowe na stronach produktów" accent="success" />
        <Toggle
          checked={form.smartMenuEnabled}
          onChange={(v) => setForm({ ...form, smartMenuEnabled: v })}
          label="Inteligentne menu"
          description="Na stronie produktu zastępuje główne menu dedykowanym logo produktu + przyciskiem CTA (dodaj do koszyka)"
        />
      </div>

      {/* Promo Bar */}
      <div className="card">
        <SectionHeader icon={Megaphone} title="Promo Bar" description="Pasek promocyjny wyświetlany nad nawigacją" accent="warning" />
        <Toggle
          checked={form.promoBar.enabled}
          onChange={(v) => setForm({ ...form, promoBar: { ...form.promoBar, enabled: v } })}
          label="Promo Bar nad nawigacją"
          description="Dodatkowy pasek informacyjny nad belką menu"
        />

        {form.promoBar.enabled && (
          <div className="mt-4 space-y-3">
            {/* Colors */}
            <div className="flex gap-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Kolor tła</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={safeColorForPicker(form.promoBar.backgroundColor, '#1a1a2e')}
                    onChange={(e) => setForm({ ...form, promoBar: { ...form.promoBar, backgroundColor: e.target.value } })}
                    className="w-10 h-10 rounded border border-border bg-transparent cursor-pointer"
                  />
                  <input
                    type="text"
                    value={form.promoBar.backgroundColor}
                    onChange={(e) => setForm({ ...form, promoBar: { ...form.promoBar, backgroundColor: prefixHexColor(e.target.value) } })}
                    onBlur={(e) => setForm({ ...form, promoBar: { ...form.promoBar, backgroundColor: normalizeHexColor(e.target.value) } })}
                    className="input w-28 text-sm font-mono"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Kolor tekstu</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={safeColorForPicker(form.promoBar.textColor, '#ffffff')}
                    onChange={(e) => setForm({ ...form, promoBar: { ...form.promoBar, textColor: e.target.value } })}
                    className="w-10 h-10 rounded border border-border bg-transparent cursor-pointer"
                  />
                  <input
                    type="text"
                    value={form.promoBar.textColor}
                    onChange={(e) => setForm({ ...form, promoBar: { ...form.promoBar, textColor: prefixHexColor(e.target.value) } })}
                    onBlur={(e) => setForm({ ...form, promoBar: { ...form.promoBar, textColor: normalizeHexColor(e.target.value) } })}
                    className="input w-28 text-sm font-mono"
                  />
                </div>
              </div>
            </div>

            {/* Messages list */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-text-secondary">
                  Komunikaty ({form.promoBar.messages.length}/3)
                </label>
                {form.promoBar.messages.length < 3 && (
                  <button
                    type="button"
                    onClick={addMessage}
                    className="btn-secondary text-xs flex items-center gap-1.5"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Dodaj komunikat
                  </button>
                )}
              </div>

              <div className="space-y-3">
                {form.promoBar.messages.map((msg, idx) => (
                  <div key={idx} className="p-3 rounded-lg border border-border-subtle bg-surface-raised/50 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-text-muted font-medium">Komunikat #{idx + 1}</span>
                      {form.promoBar.messages.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeMessage(idx)}
                          className="p-1 hover:bg-danger/10 rounded text-text-secondary hover:text-danger transition-colors"
                          title="Usuń komunikat"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      value={msg.text}
                      onChange={(e) => updateMessage(idx, { text: e.target.value })}
                      placeholder="np. Darmowa dostawa od 200 zł!"
                      className="input w-full"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={msg.linkUrl || ''}
                        onChange={(e) => updateMessage(idx, { linkUrl: e.target.value || null })}
                        placeholder="URL linku (opcjonalnie)"
                        className="input flex-1"
                      />
                      <input
                        type="text"
                        value={msg.linkText || ''}
                        onChange={(e) => updateMessage(idx, { linkText: e.target.value || null })}
                        placeholder="Tekst linku"
                        className="input w-40"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Carousel settings — visible only with 2+ messages */}
            {validMessages.length > 1 && (
              <div className="p-3 rounded-lg border border-border-subtle bg-surface-raised/50 space-y-3">
                <div className="text-xs font-medium text-text-secondary">Ustawienia karuzeli</div>
                <div className="flex items-center justify-between">
                  <label className="text-sm text-text-secondary">Automatyczne przewijanie</label>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, promoBar: { ...form.promoBar, autoRotate: !form.promoBar.autoRotate } })}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${
                      form.promoBar.autoRotate ? 'bg-accent-primary' : 'bg-surface-muted'
                    }`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${form.promoBar.autoRotate ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                </div>
                {form.promoBar.autoRotate && (
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-sm text-text-secondary">Interwał (sekundy)</label>
                    <input
                      type="number"
                      min={2}
                      max={60}
                      value={form.promoBar.rotateIntervalSec}
                      onChange={(e) => setForm({ ...form, promoBar: { ...form.promoBar, rotateIntervalSec: Math.max(2, Math.min(60, parseInt(e.target.value, 10) || 5)) } })}
                      className="input w-20 text-sm"
                    />
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <label className="text-sm text-text-secondary">Pokaż strzałki &lt; &gt;</label>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, promoBar: { ...form.promoBar, showArrows: !form.promoBar.showArrows } })}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${
                      form.promoBar.showArrows ? 'bg-accent-primary' : 'bg-surface-muted'
                    }`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${form.promoBar.showArrows ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <label className="text-sm text-text-secondary">Animacja</label>
                  <select
                    value={form.promoBar.animation}
                    onChange={(e) => setForm({ ...form, promoBar: { ...form.promoBar, animation: e.target.value as 'fade' | 'slide' } })}
                    className="input text-sm w-36"
                  >
                    <option value="fade">Przenikanie</option>
                    <option value="slide">Slide</option>
                  </select>
                </div>
              </div>
            )}

            {/* Zachowaj zawsze widoczny — dostępne niezależnie od liczby wiadomości */}
            <div className="flex items-center justify-between p-3 rounded-lg border border-border-subtle bg-surface-raised/50">
              <div>
                <label className="text-sm text-text-secondary">Zachowaj zawsze widoczny</label>
                <p className="text-xs text-text-muted mt-0.5">Pasek podąża za użytkownikiem podczas przewijania strony</p>
              </div>
              <button
                type="button"
                onClick={() => setForm({ ...form, promoBar: { ...form.promoBar, alwaysVisible: !form.promoBar.alwaysVisible } })}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${
                  form.promoBar.alwaysVisible ? 'bg-accent-primary' : 'bg-surface-muted'
                }`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${form.promoBar.alwaysVisible ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* Preview */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Podgląd</label>
              <div
                className="rounded-lg py-2 px-4 flex items-center justify-center gap-3 text-sm"
                style={{
                  backgroundColor: form.promoBar.backgroundColor,
                  color: form.promoBar.textColor,
                }}
              >
                {validMessages.length > 1 && form.promoBar.showArrows && (
                  <button
                    type="button"
                    onClick={() => setPreviewIdx((i) => (i - 1 + validMessages.length) % validMessages.length)}
                    className="p-0.5 opacity-70 hover:opacity-100 transition-opacity"
                    style={{ color: form.promoBar.textColor }}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                )}
                <span className="flex-1 text-center">
                  {validMessages.length > 0 ? (
                    <>
                      {validMessages[previewIdx]?.text}
                      {validMessages[previewIdx]?.linkText && (
                        <span className="ml-2 underline cursor-pointer">{validMessages[previewIdx]?.linkText}</span>
                      )}
                    </>
                  ) : (
                    <span className="opacity-50">Tekst promo bar</span>
                  )}
                </span>
                {validMessages.length > 1 && form.promoBar.showArrows && (
                  <button
                    type="button"
                    onClick={() => setPreviewIdx((i) => (i + 1) % validMessages.length)}
                    className="p-0.5 opacity-70 hover:opacity-100 transition-opacity"
                    style={{ color: form.promoBar.textColor }}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
              {validMessages.length > 1 && (
                <div className="mt-2 flex items-center justify-center gap-1.5">
                  {validMessages.map((_, i) => (
                    <span
                      key={i}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${i === previewIdx ? 'bg-accent-primary' : 'bg-gray-600'}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <SettingsSaveBar saving={saving} status={saveStatus} onSave={handleSave} label="Zapisz nawigację" />

      {/* WP Media Picker Modal */}
      {mediaPickerFor && (
        <WpMediaPickerModal
          integrationId={integrationId}
          onSelect={(url) => {
            if (mediaPickerFor === 'light') {
              setForm({ ...form, logoLightUrl: url });
            } else {
              setForm({ ...form, logoDarkUrl: url });
            }
            setMediaPickerFor(null);
          }}
          onClose={() => setMediaPickerFor(null)}
        />
      )}
    </div>
  );
}
